import React from 'react';
import { Loader, Pagination } from '@mantine/core';
import {
  TENBIS_DIRECTION_LABELS,
  TENBIS_KIND_LABELS,
  TENBIS_STATUS_LABELS,
  resolveStatusClass,
} from '../monitoring-page.constants';
import { formatDateTime } from '../monitoring-page.utils';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type TenbisCallLogsTableProps = Pick<
  UseMonitoringPage,
  | 'tenbisRows'
  | 'isTenbisLoading'
  | 'hasLoadedTenbis'
  | 'tenbisTotalPages'
  | 'tenbisPage'
  | 'setTenbisPage'
  | 'openTenbisDetail'
>;

const statusBadgeClass = (status: string) => {
  if (status === 'success') return classes.levelBadgeMuted;
  if (status === 'timeout') return classes.levelBadgeWarn;
  return classes.levelBadgeError;
};

const httpBadgeClass = (statusClass: '4xx' | '5xx' | null) =>
  statusClass === '5xx' ? classes.statusBadge5xx : statusClass === '4xx' ? classes.statusBadge4xx : classes.levelBadgeMuted;

export const TenbisCallLogsTable: React.FC<TenbisCallLogsTableProps> = ({
  tenbisRows,
  isTenbisLoading,
  hasLoadedTenbis,
  tenbisTotalPages,
  tenbisPage,
  setTenbisPage,
  openTenbisDetail,
}) => {
  if (isTenbisLoading && !hasLoadedTenbis) {
    return (
      <div className={classes.loadingWrap}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={classes.tableWrap}>
      <table className={classes.table} data-testid="monitoring-tenbis-table">
        <thead>
          <tr>
            <th>{'תאריך ושעה'}</th>
            <th>{'עסק'}</th>
            <th>{'כיוון'}</th>
            <th>{'סוג'}</th>
            <th>{'סטטוס'}</th>
            <th>{'קוד HTTP'}</th>
            <th>{'זמן תגובה'}</th>
            <th>{'מזהה בקשה'}</th>
            <th>{'שגיאה'}</th>
          </tr>
        </thead>
        <tbody>
          {tenbisRows.map((row) => (
            <tr
              key={row.id}
              className={classes.clickableRow}
              role="button"
              tabIndex={0}
              data-testid={`monitoring-tenbis-row-${row.id}`}
              onClick={() => openTenbisDetail(row)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openTenbisDetail(row);
                }
              }}
            >
              <td data-label={'תאריך ושעה'}>{formatDateTime(row.createdAt)}</td>
              <td data-label={'עסק'}>{row.companyName ?? row.tenantId ?? '-'}</td>
              <td data-label={'כיוון'}>{TENBIS_DIRECTION_LABELS[row.direction] ?? row.direction}</td>
              <td data-label={'סוג'}>{TENBIS_KIND_LABELS[row.kind] ?? row.kind}</td>
              <td data-label={'סטטוס'}>
                <span className={`${classes.detailKindBadge} ${statusBadgeClass(row.status)}`}>
                  {TENBIS_STATUS_LABELS[row.status] ?? row.status}
                </span>
              </td>
              <td data-label={'קוד HTTP'}>
                {row.httpStatus != null ? (
                  <span className={`${classes.detailKindBadge} ${httpBadgeClass(resolveStatusClass(row.httpStatus))}`}>
                    {row.httpStatus}
                  </span>
                ) : (
                  '-'
                )}
              </td>
              <td data-label={'זמן תגובה'}>{row.latencyMs != null ? `${row.latencyMs} ms` : '-'}</td>
              <td data-label={'מזהה בקשה'}>{row.requestId ?? '-'}</td>
              <td data-label={'שגיאה'}>
                <div className={classes.errorMessageCell}>{row.errorMessage ?? '-'}</div>
              </td>
            </tr>
          ))}
          {tenbisRows.length === 0 && (
            <tr>
              <td colSpan={9} className={classes.empty}>
                {'אין רשומות מעקב תן ביס לסינון הנוכחי.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {tenbisTotalPages > 1 && (
        <div className={classes.pagination}>
          <Pagination total={tenbisTotalPages} value={tenbisPage} onChange={setTenbisPage} size="sm" />
        </div>
      )}
    </div>
  );
};
